import { Box, useMediaQuery, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { AuthBlurredBackground } from "./AuthBlurredBackground.tsx";
import { AuthPageMobile } from "./AuthPageMobile.tsx";

type AuthPageShellProps = {
    isDarkTheme: boolean;
    /** Shown only at `lg+`; marketing / branding column. */
    desktopSidePanel: ReactNode;
    /** Form column: typically a wrapper `Box` + `Paper`. */
    children: ReactNode;
};

/**
 * Auth routes are `position: fixed` so they do not extend `#root`’s scroll height.
 * That removes the classic double-scrollbar (`#root` + in-page) on narrow viewports.
 */
export const AuthPageShell: FC<AuthPageShellProps> = ({ isDarkTheme, desktopSidePanel, children }) => {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: theme.zIndex.appBar,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                overflow: "hidden",
                width: "100%",
                height: "100vh",
                "@supports (height: 100dvh)": {
                    height: "100dvh"
                }
            }}
        >
            <AuthBlurredBackground isDark={isDarkTheme} />

            {isLargeScreen ? (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "row",
                        minHeight: 0,
                        overflow: "hidden",
                        position: "relative",
                        zIndex: 1
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            minWidth: 0,
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden"
                        }}
                    >
                        {desktopSidePanel}
                    </Box>
                    <Box
                        sx={{
                            width: 500,
                            maxWidth: "100%",
                            flexShrink: 0,
                            alignSelf: "stretch",
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            overflowY: "auto",
                            overflowX: "hidden",
                            overscrollBehaviorY: "contain"
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            ) : (
                <AuthPageMobile>{children}</AuthPageMobile>
            )}
        </Box>
    );
};
